<template>
  <v-container fluid>
    <v-container class="mt-4">
      <v-row>
        <v-col>
          <security-selector
            v-model="eqtySelected"
            color="form-field"
            :error-messages="clErrors.security"
            :hide-details="false"
            :label="$t('security')"
            :placeholder="$t('securityBorrowerLocate.tip')"
          ></security-selector>
        </v-col>
        <v-col clas="py-0">
          <volume-input
            ref="rVolumeInput"
            label="Quantity"
            :step="1"
            tabindex
            :volume="borrowerLocateBuffer.quantity"
            @changed-error="onVolumeError"
            @changed-volume="onVolumeChange"
          ></volume-input>
        </v-col>
      </v-row>
      <v-row v-if="quantityError !== ''" align="center" class="ma-0 pa-0">
        <v-col></v-col>
        <v-col class="ma-0 pa-0 pr-1">
          <v-alert class="py-1" color="error" dense text>{{ quantityError }}</v-alert>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="ma-0 ml-8">
          <template v-if="isLoaded">
            <v-btn block color="primary" @click="requestLocates">
              <v-icon left> mdi-card-search-outline</v-icon>
              Request Locate
            </v-btn>
          </template>
        </v-col>
      </v-row>
    </v-container>

    <v-container class="mt-8" fluid>
      <borrower-locates-table
        :borrower-locates="borrowerLocates"
        @locate-intent="onBorrowLocateIntent"
      />
    </v-container>
  </v-container>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { isEmpty } from 'lodash';

import BorrowerLocatesTable from '@/modules/borrower/components/BorrowerLocatesTable.vue';
import SecuritySelector from '@/modules/orders/components/SecuritySelector.vue';
import VolumeInput from '@/modules/common/components/format-volume/VolumeInput.vue';

import { RestOperation } from '@/utils/helpers/rest';
import { BorrowIntent } from '@/utils/api/locates';
import { FormValidator, setInputFocus } from '@/utils/helpers/input-form';
import { AuctionSecurity } from '@/modules/common/models';
import { BorrowerLocate } from '@/modules/borrower/models';
import { useBorrowerLocates } from '@/modules/borrower/composables';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import logger from '@/modules/common/services/logger.service';

const { borrowerLocates, fetchBorrowerLocates, submitBorrowerLocate } = useBorrowerLocates();
const snackbar = useSnackbarManager();

const rVolumeInput = ref<InstanceType<typeof VolumeInput> | null>(null);

const isLoaded = ref(false);
const eqtySelected = ref<AuctionSecurity | null>(null);
const quantityError = ref('');
const borrowerLocateBuffer = ref(BorrowerLocate.create());
const isProcessing = ref(false);
const clErrors = ref<{ [index: string]: string }>({});

watch(eqtySelected, (selected) => {
  if (selected) {
    borrowerLocateBuffer.value.security = selected;
    clErrors.value.security = '';
  }
});

onMounted(async () => {
  borrowerLocateBuffer.value = BorrowerLocate.create();

  try {
    await fetchBorrowerLocates();
    isLoaded.value = true;
  } catch (e) {
    logger.warn(e);
  }
});

function onVolumeChange(volume: number): void {
  borrowerLocateBuffer.value.quantity = volume;
  quantityError.value = '';
}

function onVolumeError(errorStr: string): void {
  quantityError.value = errorStr;
}

async function requestLocates(): Promise<void> {
  await createLocate();
}

async function createLocate(): Promise<void> {
  if (isProcessing.value) {
    return;
  }
  isProcessing.value = true;

  try {
    clErrors.value = new FormValidator(borrowerLocateBuffer.value, ['security']).check();

    if (!isEmpty(clErrors.value)) {
      return;
    }

    if (borrowerLocateBuffer.value.quantity < 1) {
      quantityError.value = 'enter quantity to locate';
      if (rVolumeInput.value) {
        setInputFocus(rVolumeInput.value);
      }
      return;
    }

    await submitLocate();

    eqtySelected.value = null;
    borrowerLocateBuffer.value = BorrowerLocate.create();
  } finally {
    isProcessing.value = false;
  }
}

async function submitLocate(): Promise<void> {
  try {
    await submitBorrowerLocate(borrowerLocateBuffer.value, RestOperation.Create);
    snackbar.show({ message: 'Locate Requested', timeout: 2000 });
  } catch (e) {
    alert(e);
  }
}

function onBorrowLocateIntent(intent: BorrowIntent): void {
  if (intent.quantity == null) {
    return;
  }

  borrowerLocateBuffer.value.security = intent.security;
  borrowerLocateBuffer.value.quantity = intent.quantity;

  eqtySelected.value = intent.security;
}
</script>

<style lang="scss" scoped>
.aurora-drag-handle {
  font-weight: bold;
  background-color: #607d8b !important;
}
</style>
